import React, { useState } from 'react';
import { MatchState } from '../core/types';
import { createInitialMatchState, scorePoint } from '../core/scoring';
import { tennisAudio } from '../core/audio';
import { umpireVoice } from '../core/voice';
import { useI18n } from '../core/i18n';
import confetti from 'canvas-confetti';
import {
  Trophy,
  RotateCcw,
  Zap,
  Activity,
  Award,
  AlertCircle,
  Volume2,
  VolumeX,
  Flame,
  CheckCircle,
} from 'lucide-react';

export const MatchUmpireView: React.FC = () => {
  const { t } = useI18n();
  const [match, setMatch] = useState<MatchState>(() => createInitialMatchState());
  const [lastCallout, setLastCallout] = useState<string>('比赛准备就绪 · 选手1 发球 (Play)');
  const [historyStack, setHistoryStack] = useState<MatchState[]>([]);

  const handlePoint = (
    winner: 1 | 2,
    pointType: 'winner' | 'unforced_error' | 'forced_error' | 'ace' | 'double_fault' = 'winner',
    rallyLength: 'short' | 'medium' | 'long' = 'short'
  ) => {
    if (match.isMatchOver) return;

    // Save history for undo
    setHistoryStack((prev) => [...prev, JSON.parse(JSON.stringify(match))]);

    // Audio SFX
    if (pointType === 'ace') {
      tennisAudio.playHit('flat');
    } else if (pointType === 'double_fault') {
      tennisAudio.playNetChord();
    } else {
      tennisAudio.playHit('topspin');
    }

    const { nextState, callout } = scorePoint(match, winner, pointType, rallyLength);
    setMatch(nextState);
    setLastCallout(callout);

    // Voice announcement
    umpireVoice.speak(callout);

    if (nextState.isMatchOver) {
      tennisAudio.playCheer();
      confetti({ particleCount: 120, spread: 80 });
    }
  };

  const handleUndo = () => {
    if (historyStack.length === 0) return;
    const previous = historyStack[historyStack.length - 1];
    setHistoryStack((prev) => prev.slice(0, -1));
    setMatch(previous);
    setLastCallout('已撤销上一分');
  };

  const handleReset = () => {
    if (confirm('确定要重置当前比赛并清空比分数据吗？')) {
      setMatch(createInitialMatchState(match.player1Name, match.player2Name, match.isNoAd));
      setHistoryStack([]);
      setLastCallout('比赛已重置');
    }
  };

  const p1TotalPoints = match.stats.totalPointsWon[0];
  const p2TotalPoints = match.stats.totalPointsWon[1];
  const totalRallies = p1TotalPoints + p2TotalPoints || 1;

  return (
    <div className="w-full max-w-5xl mx-auto p-3 sm:p-5 space-y-5">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-900/90 p-4 rounded-2xl border border-slate-800 shadow-xl">
        <div className="flex items-center space-x-2.5">
          <div className="w-10 h-10 rounded-xl bg-lime-500/20 border border-lime-500/40 flex items-center justify-center text-lime-400">
            <Trophy className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-black text-white flex items-center gap-2">
              <span>{t.umpireTitle}</span>
              <span className="text-[10px] bg-slate-800 text-lime-400 px-2 py-0.5 rounded-full border border-slate-700">
                Live Umpire HUD
              </span>
            </h1>
            <div className="text-xs text-slate-400">
              欢乐俱乐部实时专业赛事记分、发球换区轮转与多维战术雷达
            </div>
          </div>
        </div>

        {/* Top Control Buttons */}
        <div className="flex items-center space-x-2 text-xs">
          <button
            onClick={() => setMatch((prev) => ({ ...prev, isNoAd: !prev.isNoAd }))}
            className={`px-3 py-1.5 rounded-xl border font-bold transition-all ${
              match.isNoAd
                ? 'bg-amber-500 text-slate-950 border-amber-400'
                : 'bg-slate-800 text-slate-300 border-slate-700'
            }`}
          >
            {match.isNoAd ? '⚡ No-Ad 金球制胜' : '标准 Deuce 规则'}
          </button>

          <button
            onClick={handleUndo}
            disabled={historyStack.length === 0}
            className="flex items-center space-x-1 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-300 border border-slate-700 disabled:opacity-40"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>{t.undo}</span>
          </button>

          <button
            onClick={handleReset}
            className="px-3 py-1.5 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40"
          >
            {t.resetMatch}
          </button>
        </div>
      </div>

      {/* Main Scoreboard Deck */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 p-4 sm:p-6 rounded-3xl border border-slate-700/80 shadow-2xl">
        {/* Live Callout Marquee */}
        <div className="mb-4 text-center py-2 px-4 bg-slate-950/80 rounded-2xl border border-slate-800 flex items-center justify-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-lime-400 animate-ping"></span>
          <span className="text-sm sm:text-base font-black text-lime-300 tracking-wide">
            {lastCallout}
          </span>
        </div>

        {/* Digital Tennis Board Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-800 text-[11px] text-slate-400 uppercase tracking-wider">
                <th className="py-2.5 px-3">选手 (Players)</th>
                <th className="py-2.5 px-2 text-center">发球</th>
                <th className="py-2.5 px-3 text-center">第1盘</th>
                <th className="py-2.5 px-3 text-center">第2盘</th>
                <th className="py-2.5 px-3 text-center">第3盘</th>
                <th className="py-2.5 px-4 text-center bg-slate-850/60 rounded-t-xl font-bold text-lime-400">
                  当前比分 (Points)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {/* Player 1 Row */}
              <tr className={match.currentServer === 1 ? 'bg-slate-850/30' : ''}>
                <td className="py-3 px-3 font-bold text-white flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-lime-400 inline-block shadow"></span>
                  <span className="text-sm sm:text-base">{match.player1Name}</span>
                </td>
                <td className="py-3 px-2 text-center">
                  {match.currentServer === 1 && (
                    <span className="text-xs bg-lime-500/20 text-lime-300 px-2 py-0.5 rounded-full border border-lime-500/40 font-bold">
                      🎾 {match.servingSide === 'deuce' ? '右区' : '左区'}
                    </span>
                  )}
                </td>
                <td className="py-3 px-3 text-center font-mono text-base font-black text-slate-200">
                  {match.sets[0]?.player1Games ?? '-'}
                </td>
                <td className="py-3 px-3 text-center font-mono text-base font-black text-slate-200">
                  {match.sets[1]?.player1Games ?? '-'}
                </td>
                <td className="py-3 px-3 text-center font-mono text-base font-black text-slate-200">
                  {match.sets[2]?.player1Games ?? '-'}
                </td>
                <td className="py-3 px-4 text-center bg-slate-850/60">
                  <span className="text-2xl sm:text-3xl font-black font-mono text-lime-400 tracking-wider">
                    {match.isTiebreak || match.isSuperTiebreak
                      ? match.currentServer === 1
                        ? match.currentGame.serverTiebreakPoints
                        : match.currentGame.receiverTiebreakPoints
                      : match.currentServer === 1
                      ? match.currentGame.serverPoints
                      : match.currentGame.receiverPoints}
                  </span>
                </td>
              </tr>

              {/* Player 2 Row */}
              <tr className={match.currentServer === 2 ? 'bg-slate-850/30' : ''}>
                <td className="py-3 px-3 font-bold text-white flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-rose-500 inline-block shadow"></span>
                  <span className="text-sm sm:text-base">{match.player2Name}</span>
                </td>
                <td className="py-3 px-2 text-center">
                  {match.currentServer === 2 && (
                    <span className="text-xs bg-rose-500/20 text-rose-300 px-2 py-0.5 rounded-full border border-rose-500/40 font-bold">
                      🎾 {match.servingSide === 'deuce' ? '右区' : '左区'}
                    </span>
                  )}
                </td>
                <td className="py-3 px-3 text-center font-mono text-base font-black text-slate-200">
                  {match.sets[0]?.player2Games ?? '-'}
                </td>
                <td className="py-3 px-3 text-center font-mono text-base font-black text-slate-200">
                  {match.sets[1]?.player2Games ?? '-'}
                </td>
                <td className="py-3 px-3 text-center font-mono text-base font-black text-slate-200">
                  {match.sets[2]?.player2Games ?? '-'}
                </td>
                <td className="py-3 px-4 text-center bg-slate-850/60 rounded-b-xl">
                  <span className="text-2xl sm:text-3xl font-black font-mono text-rose-400 tracking-wider">
                    {match.isTiebreak || match.isSuperTiebreak
                      ? match.currentServer === 2
                        ? match.currentGame.serverTiebreakPoints
                        : match.currentGame.receiverTiebreakPoints
                      : match.currentServer === 2
                      ? match.currentGame.serverPoints
                      : match.currentGame.receiverPoints}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Quick Umpire Scoring Action Pads */}
        {!match.isMatchOver ? (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Player 1 Scoring Pad */}
            <div className="p-4 rounded-2xl bg-slate-950/80 border border-lime-500/30 space-y-2.5">
              <div className="text-xs font-bold text-lime-400 flex items-center justify-between">
                <span>{match.player1Name} 得分通道</span>
                <span className="text-[10px] text-slate-400">点击即时计入</span>
              </div>
              <button
                onClick={() => handlePoint(1, 'winner')}
                className="w-full py-3 rounded-xl bg-lime-500 hover:bg-lime-400 text-slate-950 font-black text-base shadow-lg transition-all active:scale-[0.98]"
              >
                +1 得分 (制胜分 / Winner)
              </button>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <button
                  onClick={() => handlePoint(1, 'ace')}
                  className="py-2 rounded-lg bg-slate-900 hover:bg-slate-850 border border-slate-800 text-lime-300 font-bold"
                >
                  ⚡ ACE 发球
                </button>
                <button
                  onClick={() => handlePoint(1, 'unforced_error')}
                  className="py-2 rounded-lg bg-slate-900 hover:bg-slate-850 border border-slate-800 text-slate-300"
                >
                  对手非受迫失误
                </button>
              </div>
            </div>

            {/* Player 2 Scoring Pad */}
            <div className="p-4 rounded-2xl bg-slate-950/80 border border-rose-500/30 space-y-2.5">
              <div className="text-xs font-bold text-rose-400 flex items-center justify-between">
                <span>{match.player2Name} 得分通道</span>
                <span className="text-[10px] text-slate-400">点击即时计入</span>
              </div>
              <button
                onClick={() => handlePoint(2, 'winner')}
                className="w-full py-3 rounded-xl bg-rose-500 hover:bg-rose-400 text-white font-black text-base shadow-lg transition-all active:scale-[0.98]"
              >
                +1 得分 (制胜分 / Winner)
              </button>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <button
                  onClick={() => handlePoint(2, 'ace')}
                  className="py-2 rounded-lg bg-slate-900 hover:bg-slate-850 border border-slate-800 text-rose-300 font-bold"
                >
                  ⚡ ACE 发球
                </button>
                <button
                  onClick={() => handlePoint(2, 'unforced_error')}
                  className="py-2 rounded-lg bg-slate-900 hover:bg-slate-850 border border-slate-800 text-slate-300"
                >
                  对手非受迫失误
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 p-5 bg-gradient-to-r from-lime-950 to-slate-950 rounded-2xl border border-lime-500/40 text-center space-y-2">
            <Trophy className="w-10 h-10 text-amber-400 mx-auto" />
            <h3 className="text-lg font-black text-white">
              恭喜 {match.winner === 1 ? match.player1Name : match.player2Name} 荣耀胜出！
            </h3>
            <p className="text-xs text-slate-300">
              全场比赛已正式结束，比赛数据已自动归档至俱乐部战绩雷达！
            </p>
          </div>
        )}
      </div>

      {/* Live Match Stats Dashboard */}
      <div className="bg-slate-900/80 p-5 rounded-2xl border border-slate-800 space-y-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
          <Activity className="w-4 h-4 text-lime-400" />
          <span>多维技术统计雷达 (Match Stats Radar)</span>
        </h3>

        <div className="space-y-3 text-xs">
          {/* Total Points Won */}
          <div>
            <div className="flex justify-between text-slate-300 mb-1">
              <span>{p1TotalPoints} 分</span>
              <span className="text-slate-400 font-semibold">总得分 (Total Points)</span>
              <span>{p2TotalPoints} 分</span>
            </div>
            <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden flex">
              <div
                className="bg-lime-500 h-full transition-all"
                style={{ width: `${(p1TotalPoints / totalRallies) * 100}%` }}
              ></div>
              <div
                className="bg-rose-500 h-full transition-all"
                style={{ width: `${(p2TotalPoints / totalRallies) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Aces & Winners */}
          <div className="grid grid-cols-3 gap-2 text-center p-3 bg-slate-950 rounded-xl border border-slate-800/80">
            <div>
              <div className="font-bold text-lime-400 text-sm">{match.stats.aces[0]} / {match.stats.winners[0]}</div>
              <div className="text-[10px] text-slate-500">ACE / 制胜分</div>
            </div>
            <div className="text-slate-400 font-semibold text-[11px] self-center">
              攻击力指标
            </div>
            <div>
              <div className="font-bold text-rose-400 text-sm">{match.stats.aces[1]} / {match.stats.winners[1]}</div>
              <div className="text-[10px] text-slate-500">ACE / 制胜分</div>
            </div>
          </div>

          {/* Unforced Errors */}
          <div className="grid grid-cols-3 gap-2 text-center p-3 bg-slate-950 rounded-xl border border-slate-800/80">
            <div>
              <div className="font-bold text-amber-400 text-sm">{match.stats.unforcedErrors[0]}</div>
              <div className="text-[10px] text-slate-500">非受迫失误 (UE)</div>
            </div>
            <div className="text-slate-400 font-semibold text-[11px] self-center">
              失误控制
            </div>
            <div>
              <div className="font-bold text-amber-400 text-sm">{match.stats.unforcedErrors[1]}</div>
              <div className="text-[10px] text-slate-500">非受迫失误 (UE)</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
